import type { ClientUnit, Risk } from '../../types/api';
import { extractRiskCityName } from '../geospatial';
import { filterRisksByUnitScope, unitScopeKeysFromClientUnits } from './unitScopeRiskFiltering';
import {
  isWithinPeriodWindow,
  resolvePeriodWindow,
  type PeriodMode,
  type PeriodWindow,
} from '../calculations/unitFinancials';

export const SWARM_FEED_ALL = '__all__';

export type SwarmFeedChart = 'portfolio' | 'incomeExpenses' | 'netCashflow';

export type SwarmFeedOption = {
  key: string;
  label: string;
};

export type SwarmFeedYearRange = {
  start: string;
  end: string;
};

export type SwarmFeedQuery = {
  chart: SwarmFeedChart;
  locationKey: string;
  operatingModelKey: string;
  periodMode: PeriodMode;
  anchorDateKey: string | null;
  yearRange: SwarmFeedYearRange | null;
};

const IMPACT_TARGETS: Record<SwarmFeedChart, ReadonlySet<string>> = {
  portfolio: new Set(['NMV', 'BOTH']),
  incomeExpenses: new Set(['NOI', 'BOTH']),
  netCashflow: new Set(['NOI', 'BOTH']),
};

const ORIGINATOR_CODES: Record<string, { code: string; label: string }> = {
  a: { code: 'A', label: 'Analyst' },
  analyst: { code: 'A', label: 'Analyst' },
  c: { code: 'C', label: 'Community' },
  community: { code: 'C', label: 'Community' },
  o: { code: 'O', label: 'User' },
  user: { code: 'O', label: 'User' },
};

function titleCaseWords(value: string): string {
  return value
    .trim()
    .split(/\s+/)
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ''))
    .filter(Boolean)
    .join(' ');
}

function publicationInstant(risk: Risk): Date | null {
  const raw = risk.publication_date?.trim();
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Chart months are stored on the 1st. Publications anywhere in that month still belong to it. */
function publicationWindow(periodMode: PeriodMode, anchorDateKey: string | null): PeriodWindow {
  const periodWindow = resolvePeriodWindow(periodMode, anchorDateKey);
  const end = new Date(
    periodWindow.end.getFullYear(),
    periodWindow.end.getMonth() + 1,
    0,
    23,
    59,
    59,
    999,
  );
  return { ...periodWindow, end };
}

function isInsideYearRange(date: Date, yearRange: SwarmFeedYearRange): boolean {
  const start = yearRange.start <= yearRange.end ? yearRange.start : yearRange.end;
  const end = yearRange.start <= yearRange.end ? yearRange.end : yearRange.start;
  const year = String(date.getFullYear());
  return year >= start && year <= end;
}

function matchesImpact(chart: SwarmFeedChart, impactTarget: string | undefined): boolean {
  const normalized = (impactTarget ?? '').trim().toUpperCase();
  return IMPACT_TARGETS[chart].has(normalized);
}

export function swarmRecordLocation(risk: Risk): SwarmFeedOption | null {
  const countryRaw = (risk.country ?? '').trim();
  const cityRaw = extractRiskCityName(risk.location) || (risk.location ?? '').trim();
  if (!countryRaw && !cityRaw) return null;

  const countryLabel = titleCaseWords(countryRaw);
  const cityLabel = titleCaseWords(cityRaw);
  return {
    key: `${countryRaw.toLowerCase()}|${cityRaw.toLowerCase()}`,
    label: [countryLabel, cityLabel].filter(Boolean).join(', '),
  };
}

export function swarmOperatingModel(risk: Risk): SwarmFeedOption | null {
  const label = (risk.operating_mode ?? '').trim();
  if (!label) return null;
  return { key: label.toLowerCase(), label };
}

export function swarmOriginator(originator: string | undefined): { code: string; label: string } | null {
  const raw = originator?.trim();
  if (!raw) return null;
  const key = raw.toLowerCase();
  const known = ORIGINATOR_CODES[key];
  if (known) return known;
  if (key.startsWith('analyst')) return ORIGINATOR_CODES.analyst;
  if (key.startsWith('community')) return ORIGINATOR_CODES.community;
  if (key.startsWith('user')) return ORIGINATOR_CODES.user;
  return { code: raw, label: raw };
}

export function swarmSourceLabel(risk: Risk): string {
  return risk.source_type?.trim() || risk.source_document?.trim() || '';
}

export function swarmSourceUrl(raw: string | undefined | null): string | null {
  const value = raw?.trim();
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('//')) return `https:${value}`;
  return `https://${value}`;
}

function uniqueOptions(options: Array<SwarmFeedOption | null>): SwarmFeedOption[] {
  const byKey = new Map<string, SwarmFeedOption>();
  options.forEach((option) => {
    if (!option || byKey.has(option.key)) return;
    byKey.set(option.key, option);
  });
  return Array.from(byKey.values()).sort((left, right) => left.label.localeCompare(right.label));
}

/** First cut: keep swarm rows that match a client unit's country and operating model. */
export function filterSwarmByUnits(
  risks: Risk[],
  units: readonly Pick<ClientUnit, 'country' | 'operating_model'>[],
): Risk[] {
  const keys = unitScopeKeysFromClientUnits(units);
  if (keys.countryKeys.size === 0) return [];
  return filterRisksByUnitScope(risks, keys);
}

export function swarmLocationOptions(risks: Risk[]): SwarmFeedOption[] {
  return uniqueOptions(risks.map((risk) => swarmRecordLocation(risk)));
}

export function swarmOperatingModelOptions(risks: Risk[]): SwarmFeedOption[] {
  return uniqueOptions(risks.map((risk) => swarmOperatingModel(risk)));
}

export function filterSwarmFeed(risks: Risk[], query: SwarmFeedQuery): Risk[] {
  const window = publicationWindow(query.periodMode, query.anchorDateKey);

  return risks
    .filter((risk) => {
      if (!matchesImpact(query.chart, risk.impact_target)) return false;

      const published = publicationInstant(risk);
      if (!published || !isWithinPeriodWindow(published, window)) return false;
      if (query.yearRange && !isInsideYearRange(published, query.yearRange)) return false;

      if (query.locationKey !== SWARM_FEED_ALL) {
        const location = swarmRecordLocation(risk);
        if (!location || location.key !== query.locationKey) return false;
      }

      if (query.operatingModelKey !== SWARM_FEED_ALL) {
        const operatingModel = swarmOperatingModel(risk);
        if (!operatingModel || operatingModel.key !== query.operatingModelKey) return false;
      }

      return true;
    })
    .sort((left, right) => {
      const leftTime = publicationInstant(left)?.getTime() ?? -Infinity;
      const rightTime = publicationInstant(right)?.getTime() ?? -Infinity;
      if (leftTime !== rightTime) return rightTime - leftTime;
      return (left.description ?? '').localeCompare(right.description ?? '');
    });
}
